import forecastingService from '@/services/forecastingService'
import ForecastingUtilService from '@/services/forecastingUtilService'
import store from "@/store"
import ForecastingOverviewCalendarModes from '@/helpers/forecastingOverviewCalendarModes'
import momentCache from "@/services/momentCache"
import ForecastingStoreWorker from "@/stores/forecastingStoreWorker"
import * as Constants from '@/helpers/constants';

export default {
    namespaced: true,
    state: {
        selectedDate: momentCache.getMoment().format('YYYY-MM-DD'),
        loadingFinancialYearCalendarData: false,
        financialYearCalendarData: null,
        publicHolidayData: null,
        loadingPlants: false,
        plantData: null,
        loadingNoteAlertCategories: false,
        noteAlertCategories: null,
        loadingUnsentStatus: false,
        unsentStatus: null,
        selectedPlantId: null,
        selectedPlantRegion: null,
        loadingWeatherData: false,
        weatherData: null,
        selectedCategoryId: null,
        selectedUOM: null,
        loadingCropVarieties: false,
        filterDivisionIds: [],
        filterLocationIds: [],
        filterVarietyIds: [],
        crops: [],
        varieties: [],
        forecasts: [],
        yields: [],
        notes: [],
        nuPointSprays: [],
        selectedCalendarMode: ForecastingOverviewCalendarModes.Week,
        filterLocationName: null,
        detailedWeeks: [],
        listSortedItems: [],
        listNumberOfLocationsFiltered: 0,
        calendarSortedItems: [],
        calendarNumberOfLocationsFiltered: 0,
        calendarDayTotals: [],
        calendarWeekTotals: [],
        exportLoading: false,
        showProfileValues: false,
        showYieldValues: false,
        showAllActiveLocations: false,
        showNavigationAndFilters: true
    },
    getters: {
        today(){
            return momentCache.getMoment().format('YYYY-MM-DD');
        },
        oneWeekBeforeSelected(state) {
            return momentCache.createMoment(state.selectedDate)
                .add(-1, 'weeks')
                .startOf('week')
                .format('YYYY-MM-DD');
        },
        startOfSelectedWeek(state){
            return momentCache.createMoment(state.selectedDate)
                .startOf('week')
                .format('YYYY-MM-DD');
        },
        endOfSixWeeksFromSelected(state){
            return momentCache.createMoment(state.selectedDate)
                .add(6, 'weeks')
                .endOf('week')
                .format('YYYY-MM-DD')
        },
        editable(state){
            if(state.selectedPlantId) {
                return store.getters["UserStore/isAuthorised"]({accessRights:[{PlantID: state.selectedPlantId, Access:'Forecasting and Rostering'}]})
            } else {
                return false;
            }
        },
        uomScaleFactor(state) {
            if (state.selectedPlantId && state.selectedCategoryId && state.selectedUOM) {
                let plant = state.plantData.find(x => x.plantId === state.selectedPlantId);
                let category = plant.productCategories.find(x => x.productCategoryId === state.selectedCategoryId);
                let defaultPlantUnit = category.defaultPlantUnit;

                if (defaultPlantUnit) {
                    if (state.selectedUOM === Constants.HARVESTPLAN_UOM_ENUM.KG.ID) {
                        return 1;
                    }

                    if (state.selectedUOM === Constants.HARVESTPLAN_UOM_ENUM.TRAYSPUNNETS.ID) {
                        return 1 / defaultPlantUnit.aveKgsPerUnit / (defaultPlantUnit.numInTray || 1);
                    }
                } else {
                    return 1;
                }
            } else {
                return 1;
            }
        }
    },
    mutations: {
        setSelectedPlantId(state, selectedPlantId){
            state.selectedPlantId = selectedPlantId;
            // handling for mapping public holidays to the details week based on selected plant
            if(selectedPlantId != null) {
                var applicableRegion = state.plantData.find(p=>(p.plantId == selectedPlantId)).Region
                state.applicableRegion = applicableRegion
            }
        },
        setSelectedCategoryId(state, selectedCategoryId){
            state.selectedCategoryId = selectedCategoryId;
        },
        setFilterDivisionIds(state, filterDivisionIds){
            state.filterDivisionIds.splice(0, state.filterDivisionIds.length, ...filterDivisionIds)
        },
        setFilterLocationIds(state, filterLocationIds){
            state.filterLocationIds.splice(0, state.filterLocationIds.length, ...filterLocationIds)
        },
        setFilterVarietyIds(state, filterVarietyIds){
            state.filterVarietyIds.splice(0, state.filterVarietyIds.length, ...filterVarietyIds)
        },
        setFilterUOM(state, selectedUOM) {
            state.selectedUOM = selectedUOM;
        },
        setSelectedDate(state, date){
            state.selectedDate = date;
        },
        setFilterLocationName(state, filterLocationName){
            state.filterLocationName = filterLocationName;
        },
        setSelectedCalendarMode(state, selectedCalendarMode){
            state.selectedCalendarMode = selectedCalendarMode;
        },
        setShowProfileValues(state, showProfileValues) {
            state.showProfileValues = showProfileValues;
        },
        setShowYieldValues(state, showYieldValues) {
            state.showYieldValues = showYieldValues;
        },
        setShowAllActiveLocations(state, showActiveLocations) {
            state.showAllActiveLocations = showActiveLocations;
        },
        setShowNavigationAndFilters(state, showNavigationAndFilters) {
            state.showNavigationAndFilters = showNavigationAndFilters;
        }
    },
    actions: {
        async loadFinancialYearCalendarData({ state }){
            state.loadingFinancialYearCalendarData = true;

            try {
                let financialYearCalendarData = await forecastingService.getFinancialYearCalendarsData();

                if(financialYearCalendarData == null){
                    financialYearCalendarData = [];
                }
                state.financialYearCalendarData = financialYearCalendarData
            } catch(err) {
                console.log(err);
            }

            state.loadingFinancialYearCalendarData = false;
        },
        async loadPlants({ state }, selectedPlantId){
            state.loadingPlants = true;

            try {
                let plantData = await forecastingService.getPlantData(selectedPlantId);
                let holidays = await forecastingService.getPublicHolidayData(state.selectedDate, selectedPlantId)
                if(plantData == null){
                    plantData = [];
                }
                state.publicHolidayData = holidays;
                state.plantData = plantData;
            } catch(err) {
                console.log(err);
            }

            state.loadingPlants = false;
        },
        async loadNoteAlertCategories({ state }) {
            state.loadingNoteAlertCategories = true;

            try {
                let noteAlertCategories = await forecastingService.getNoteAlertCategories();
                if(noteAlertCategories == null){
                    noteAlertCategories = [];
                }

                state.noteAlertCategories = noteAlertCategories;
            } catch(err) {
                console.log(err);
            }
          
            state.loadingNoteAlertCategories = false;
        },
        async loadUnsentStatus({ state }){
            state.loadingUnsentStatus = true;

            try {
                state.unsentStatus = await forecastingService.getUnsentStatus();
            } catch (err) {
                console.log(err);
            }

            state.loadingUnsentStatus = false;
        },
        async loadWeatherData({ state, getters }){
            state.loadingWeatherData = true;

            try {
                state.weatherData = await forecastingService.getWeatherData(
                    state.selectedPlantId,
                    getters.startOfSelectedWeek,
                    getters.endOfSixWeeksFromSelected
                );
            } catch (err) {
                console.log(err);
            }

            state.loadingWeatherData = false;
        },
        async loadCropVarieties({ state, getters }){
            state.loadingCropVarieties = true;

            try {
                let cropVarietyData = await forecastingService.getCropVarietyData(
                    state.selectedPlantId, 
                    state.selectedCategoryId,
                    getters.oneWeekBeforeSelected,
                    getters.endOfSixWeeksFromSelected
                );

                state.crops.splice(0, state.crops.length, ...cropVarietyData.crops);
                state.varieties.splice(0, state.varieties.length, ...cropVarietyData.varieties);
                state.forecasts.splice(0, state.forecasts.length, ...cropVarietyData.forecasts);
                state.yields.splice(0, state.yields.length, ... cropVarietyData.yields);
                state.notes.splice(0, state.notes.length, ...cropVarietyData.notes);
                state.nuPointSprays.splice(0, state.nuPointSprays.length, ...cropVarietyData.nuPointSprays);
                state.weekViewScrollPosition = 0;
                state.dayViewScrollPosition = 0;
            } catch (err) {
                console.log(err);
            }
            
            state.loadingCropVarieties = false;
        },
        async clearCropVarieties({ state }){
            state.loadingCropVarieties = true;

            try {
                state.crops.splice(0, state.crops.length);
                state.varieties.splice(0, state.varieties.length);
                state.forecasts.splice(0, state.forecasts.length);
                state.yields.splice(0, state.yields.length);
                state.notes.splice(0, state.notes.length);
                state.nuPointSprays.splice(0, state.nuPointSprays.length);
            } catch (err) {
                console.log(err);
            }
            
            state.loadingCropVarieties = false;
        },
        async handleForecastChanged({ state, dispatch, getters }, event){       
            try {
                let forecast = await forecastingService.updateForecast(
                    event.cropLocationId,
                    event.varietyId,
                    event.date,
                    event.forecastValue != null ? Math.round(event.forecastValue / getters.uomScaleFactor) : null,
                    event.adjustedValue != null ? Math.round(event.adjustedValue / getters.uomScaleFactor) : null
                );
                var index = state.forecasts.findIndex(
                    x => x.cropLocationId == forecast.cropLocationId
                        && x.varietyId == forecast.varietyId
                        && x.forecastDate == forecast.forecastDate
                );
                if(index >= 0) {
                state.forecasts.splice(state.forecasts.findIndex(
                    x => x.cropLocationId == forecast.cropLocationId
                        && x.varietyId == forecast.varietyId
                        && x.forecastDate == forecast.forecastDate
                ), 1, forecast);
                } else {
                    state.forecasts.push(forecast);
                }
            } catch (err) {
                console.log(err);
            }

            await dispatch("loadUnsentStatus");
        },
        async handleForecastsChanged({ state, dispatch, getters }, event){
            try {
                var nonEmptyForecasts = event.forecasts.filter(f=> f.forecastValue != null || f.adjustedValue != null);
                if (event.forecasts.length > 500) {
                    console.log(event.forecasts.filter(f=> f.forecastValue == null && f.adjustedValue == null).length);
                    var allForecasts = await forecastingService.updateForecasts(nonEmptyForecasts.map(x => {
                        return {
                            cropLocationId: x.cropLocationId,
                            varietyId: x.varietyId,
                            forecastDate: x.forecastDate,
                            forecastValue: x.forecastValue != null ? Math.round(x.forecastValue / getters.uomScaleFactor) : null,
                            adjustedValue: x.adjustedValue != null ? Math.round(x.adjustedValue / getters.uomScaleFactor) : null
                        };
                    }), 
                    true,
                    state.selectedPlantId,
                    state.selectedCategoryId,
                    getters.oneWeekBeforeSelected,
                    getters.endOfSixWeeksFromSelected);

                    state.forecasts = allForecasts;
                } else {
                    var forecasts = await forecastingService.updateForecasts(nonEmptyForecasts.map(x => {
                        return {
                            cropLocationId: x.cropLocationId,
                            varietyId: x.varietyId,
                            forecastDate: x.forecastDate,
                            forecastValue: x.forecastValue != null ? Math.round(x.forecastValue / getters.uomScaleFactor) : null,
                            adjustedValue: x.adjustedValue != null ? Math.round(x.adjustedValue / getters.uomScaleFactor) : null
                        };
                    }),
                    false);

                    forecasts.forEach((f) => {
                        var index = state.forecasts.findIndex(
                            x => x.cropLocationId == f.cropLocationId
                                && x.varietyId == f.varietyId
                                && x.forecastDate == f.forecastDate
                        );
                        if (index >= 0) {
                            state.forecasts.splice(index, 1, f);
                        } else {
                            state.forecasts.push(f);
                        }
                    });
                }
            } catch (err) {
                console.log(err);
            }

            await dispatch("loadUnsentStatus");
        },
        async addNote({ state, dispatch }, note) {
              try {
                let updatedNote = await forecastingService.addUpdateNote(note);
                state.notes.push(updatedNote);
            } catch (err) {
                console.log(err);
            }

            await dispatch("loadUnsentStatus");
        },
        async saveNote({ state, dispatch }, note) {
              try {
                let updatedNote = await forecastingService.addUpdateNote(note);
             
                state.notes.splice(state.notes.findIndex(n=> momentCache.getMoment(n.raisedAt).format() == momentCache.getMoment(updatedNote.raisedAt).format()
                    && n.varietyId == updatedNote.varietyId && n.cropLocationId == updatedNote.cropLocationId), 1, updatedNote);
            } catch (err) {
                console.log(err);
            }
            
            await dispatch("loadUnsentStatus");
        },
        async handleCropVarietyChanged({ state, dispatch }, varietyModel) {
            try {
                let variety = await forecastingService.updateCropVariety(varietyModel);

                state.varieties.splice(state.varieties.findIndex(
                    x => x.cropLocationId == varietyModel.cropLocationId
                        && x.varietyId == varietyModel.varietyId
                ), 1, variety);
            } catch (err) {
                console.log(err);
            }
            
            await dispatch("loadUnsentStatus");
        },
        async exportForecast({ state }) {
            if(!state.exportLoading) {
                try {
                    state.exportLoading = true;
                    await forecastingService.exportForecastReport(
                        state.selectedPlantId,
                        state.selectedCategoryId,
                        state.filterDivisionIds, 
                        state.filterVarietyIds,
                        ForecastingUtilService.getStartOfNextWeek(),
                        ForecastingUtilService.getEndOfNextWeek()
                    );
                } catch(err) {
                    console.log(err);
                    alert("Unable to export");
                } finally {
                    state.exportLoading = false;
                }
            }
        },
        async updateData({ state, getters }, params = { all: true }) {            
            let {
                // ForecastingOverviewCalendarDay.vue
                // ForecastingOverviewCalendarWeek.vue
                detailedWeeks,

                // ForecastingOverviewCalendarDay.vue
                calendarDayTotals,

                // ForecastingOverviewCalendarDay.vue
                // ForecastingOverviewCalendarWeek.vue
                calendarWeekTotals,

                // ForecastingOverviewList.vue
                listSortedItems,

                // ForecastingOverviewList.vue
                listNumberOfLocationsFiltered,

                // ForecastingOverviewCalendarBulkUpdateDialog.vue
                // ForecastingOverviewCalendarDay.vue
                // ForecastingOverviewCalendarWeek.vue
                calendarSortedItems,

                // ForecastingOverviewCalendarDay.vue
                // ForecastingOverviewCalendarWeek.vue
                calendarNumberOfLocationsFiltered
            } = ForecastingStoreWorker.getUpdatedData(
                state.financialYearCalendarData,
                state.forecasts,
                state.yields,
                state.showAllActiveLocations,
                state.varieties,
                state.crops,
                state.notes,
                state.selectedDate,
                state.filterLocationName,
                state.filterVarietyIds,
                state.filterDivisionIds,
                state.filterLocationIds,
                getters.today,
                getters.startOfSelectedWeek,
                getters.editable,
                state.publicHolidayData,
                state.applicableRegion,
                state.nuPointSprays,
                getters.uomScaleFactor
            );
            
            if (params.all) {
                state.detailedWeeks = detailedWeeks;
                state.calendarDayTotals = calendarDayTotals;
                state.calendarWeekTotals = calendarWeekTotals;
                state.listSortedItems = listSortedItems;
                state.listNumberOfLocationsFiltered = listNumberOfLocationsFiltered;
                state.calendarSortedItems = calendarSortedItems;
                state.calendarNumberOfLocationsFiltered = calendarNumberOfLocationsFiltered;
            } else {
                state.detailedWeeks = detailedWeeks;
                state.calendarDayTotals = calendarDayTotals;
                state.calendarWeekTotals = calendarWeekTotals;
                state.listSortedItems = listSortedItems;
                state.listNumberOfLocationsFiltered = listNumberOfLocationsFiltered;

                try {
                    params.forecastDays.forEach(forecastDay=> {
                        var week = calendarSortedItems
                            .find(cst => cst.varietyId == forecastDay.varietyId && cst.cropLocationId == forecastDay.cropLocationId).forecastWeeks
                            .find(fw => fw.week == forecastDay.startOfWeek);
                        var day = week.forecastDays
                            .find(fd => fd.day == forecastDay.day);
                        state.calendarSortedItems
                            .find(cst => cst.varietyId == forecastDay.varietyId && cst.cropLocationId == forecastDay.cropLocationId).forecastWeeks
                            .find(fw => fw.week == forecastDay.startOfWeek).forecastDays
                            .find(fd => fd.day == forecastDay.day).forecastValue = day.forecastValue;
                        state.calendarSortedItems
                            .find(cst => cst.varietyId == forecastDay.varietyId && cst.cropLocationId == forecastDay.cropLocationId).forecastWeeks
                            .find(fw => fw.week == forecastDay.startOfWeek).forecastDays
                            .find(fd => fd.day == forecastDay.day).adjustedValue = day.adjustedValue;
                        state.calendarSortedItems
                            .find(cst => cst.varietyId == forecastDay.varietyId && cst.cropLocationId == forecastDay.cropLocationId).forecastWeeks
                            .find(fw => fw.week == forecastDay.startOfWeek).total = week.total;
                    })
                } catch {
                    state.calendarSortedItems = calendarSortedItems;
                }
                     
                state.calendarNumberOfLocationsFiltered = calendarNumberOfLocationsFiltered;
            }
        }
    }
}
